// components/CentralPushMotoristas.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import {
  FaPaperPlane, FaMobileAlt, FaUsers, FaUser,
  FaLink, FaHistory, FaCheckCircle, FaExclamationTriangle,
  FaChevronLeft, FaChevronRight,
  FaInfoCircle, FaBullhorn, FaBolt, FaCalendarAlt, FaClock,
  FaTrash, FaEdit, FaToggleOn, FaToggleOff, FaCalendarCheck
} from 'react-icons/fa';
import './CentralPushMotoristas.css';

const CATEGORIAS = [
  { key: 'informativo', label: 'Informativo', icon: <FaInfoCircle />, cls: 'cat-info' },
  { key: 'urgente',     label: 'Urgente',     icon: <FaBullhorn />,   cls: 'cat-urgent' },
  { key: 'alerta',      label: 'Alerta',      icon: <FaExclamationTriangle />, cls: 'cat-warn' }
];
const CATEGORIA_EMOJI = { informativo: 'ℹ️', urgente: '🚨', alerta: '⚠️' };

const RECORRENCIAS = [
  { key: 'unica',     label: 'Uma vez',      icon: <FaCalendarCheck /> },
  { key: 'intervalo', label: 'A cada X min', icon: <FaBolt /> },
  { key: 'diario',    label: 'Diário',       icon: <FaClock /> },
  { key: 'semanal',   label: 'Semanal',      icon: <FaCalendarAlt /> },
  { key: 'mensal',    label: 'Mensal',       icon: <FaCalendarAlt /> }
];

const DIAS_SEMANA = [
  { v: 0, l: 'Dom' }, { v: 1, l: 'Seg' }, { v: 2, l: 'Ter' }, { v: 3, l: 'Qua' },
  { v: 4, l: 'Qui' }, { v: 5, l: 'Sex' }, { v: 6, l: 'Sáb' }
];
const DIAS_SEMANA_FULL = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

const nowLocalInput = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

// 'YYYY-MM-DD HH:MM:SS' (parede SP) -> 'DD/MM/YYYY HH:MM' sem mexer em fuso
const fmtData = (s) => {
  if (!s) return '-';
  const [d, t = ''] = String(s).replace('T', ' ').split(' ');
  const [y, mo, da] = d.split('-');
  const hm = t.slice(0, 5);
  return `${da}/${mo}/${y}${hm ? ` ${hm}` : ''}`;
};

const descreverRecorrencia = (ag) => {
  switch (ag.tipo_recorrencia) {
    case 'unica':     return `Uma vez em ${fmtData(ag.data_unica)}`;
    case 'intervalo': return `A cada ${ag.intervalo_min} min`;
    case 'diario':    return `Diariamente às ${String(ag.hora || '').slice(0, 5)}`;
    case 'semanal':   return `Toda ${DIAS_SEMANA_FULL[ag.dia_semana] || '?'} às ${String(ag.hora || '').slice(0, 5)}`;
    case 'mensal':    return `Todo dia ${ag.dia_mes} às ${String(ag.hora || '').slice(0, 5)}`;
    default:          return '-';
  }
};

export default function CentralPushMotoristas({ user }) {
  const [activeTab, setActiveTab] = useState('novo'); // novo | agendados | historico
  const [loading, setLoading] = useState(false);

  // Histórico
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Form base
  const [targetType, setTargetType] = useState('todos');
  const [selectedDriver, setSelectedDriver] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [category, setCategory] = useState('informativo');

  // Agendamento
  const [sendMode, setSendMode] = useState('agora'); // agora | agendar
  const [recurrence, setRecurrence] = useState('unica');
  const [dataUnica, setDataUnica] = useState('');
  const [intervaloMin, setIntervaloMin] = useState(15);
  const [hora, setHora] = useState('08:00');
  const [diaSemana, setDiaSemana] = useState(1);
  const [diaMes, setDiaMes] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const [savingAgendamento, setSavingAgendamento] = useState(false);

  // Lista de agendamentos
  const [agendamentos, setAgendamentos] = useState([]);
  const [loadingAgendamentos, setLoadingAgendamentos] = useState(false);

  const [motoristas, setMotoristas] = useState([]);
  const audioRef = useRef(null);

  const titleTrim = useMemo(() => (title || '').trim(), [title]);
  const messageTrim = useMemo(() => (message || '').trim(), [message]);
  const linkTrim = useMemo(() => (link || '').trim(), [link]);

  const baseValido = useMemo(() => {
    if (!titleTrim || !messageTrim) return false;
    if (targetType === 'especifico' && !selectedDriver) return false;
    return true;
  }, [titleTrim, messageTrim, targetType, selectedDriver]);

  const canSend = baseValido;

  const canSchedule = useMemo(() => {
    if (!baseValido) return false;
    if (recurrence === 'unica' && !dataUnica) return false;
    if (recurrence === 'intervalo' && (!intervaloMin || Number(intervaloMin) < 15)) return false;
    if (['diario', 'semanal', 'mensal'].includes(recurrence) && !hora) return false;
    if (recurrence === 'mensal' && (!diaMes || diaMes < 1 || diaMes > 31)) return false;
    return true;
  }, [baseValido, recurrence, dataUnica, intervaloMin, hora, diaMes]);

  const fetchMotoristas = useCallback(async () => {
    try {
      const { data } = await api.get('/api/usuarios/selecao');
      const operacionais = Array.isArray(data) ? data.filter(u => u.perfil === 'operacional') : [];
      setMotoristas(operacionais);
    } catch (err) {
      console.error('Erro ao buscar motoristas', err);
      toast.error('Erro ao carregar lista de motoristas.');
    }
  }, []);

  const fetchHistorico = useCallback(async (page = 1) => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/api/comunicacao/push/historico?page=${page}&limit=10`);
      setHistorico(Array.isArray(data?.data) ? data.data : []);
      setCurrentPage(data?.pagination?.page || 1);
      setTotalPages(data?.pagination?.totalPages || 1);
    } catch (err) {
      console.error('Erro ao buscar histórico', err);
      toast.error('Erro ao carregar histórico.');
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const fetchAgendamentos = useCallback(async () => {
    setLoadingAgendamentos(true);
    try {
      const { data } = await api.get('/api/comunicacao/push/agendamentos');
      setAgendamentos(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Erro ao buscar agendamentos', err);
      toast.error('Erro ao carregar agendamentos.');
    } finally {
      setLoadingAgendamentos(false);
    }
  }, []);

  useEffect(() => {
    audioRef.current = new Audio('/notification.mp3');
    audioRef.current.preload = 'auto';
  }, []);

  useEffect(() => { fetchMotoristas(); }, [fetchMotoristas]);
  useEffect(() => { if (activeTab === 'historico') fetchHistorico(1); }, [activeTab, fetchHistorico]);
  useEffect(() => { if (activeTab === 'agendados') fetchAgendamentos(); }, [activeTab, fetchAgendamentos]);

  const resetForm = () => {
    setTitle(''); setMessage(''); setLink('');
    setTargetType('todos'); setSelectedDriver('');
    setCategory('informativo');
    setRecurrence('unica'); setDataUnica(''); setIntervaloMin(15);
    setHora('08:00'); setDiaSemana(1); setDiaMes(1);
    setEditingId(null); setSendMode('agora');
  };

  const handleSendPush = async () => {
    if (!canSend) {
      return toast.warn('Preencha título, mensagem e (se específico) o motorista.');
    }
    setLoading(true);
    try {
      try {
        if (audioRef.current) { audioRef.current.currentTime = 0; await audioRef.current.play(); }
      } catch { console.log('Áudio bloqueado pelo navegador'); }

      const payload = {
        alvo: targetType,
        motorista_id: targetType === 'especifico' ? Number(selectedDriver) : null,
        titulo: titleTrim,
        mensagem: messageTrim,
        acao_url: linkTrim || null,
        categoria: category
      };

      const { data } = await api.post('/api/comunicacao/push', payload);

      if (data?.warning) {
        if ((data?.enviados || 0) > 0) {
          toast.success(`Push processado! Sucesso: ${data.enviados} | Falhas: ${data.falhas || 0}`);
          toast.warn(data.warning);
        } else {
          toast.warn(data.warning);
        }
      } else {
        toast.success(`Push processado! Sucesso: ${data?.enviados || 0} | Falhas: ${data?.falhas || 0}`);
      }
      resetForm();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao enviar push.');
    } finally {
      setLoading(false);
    }
  };

  const montarPayloadAgendamento = () => ({
    alvo: targetType,
    motorista_id: targetType === 'especifico' ? Number(selectedDriver) : null,
    titulo: titleTrim,
    mensagem: messageTrim,
    acao_url: linkTrim || null,
    categoria: category,
    tipo_recorrencia: recurrence,
    intervalo_min: recurrence === 'intervalo' ? Number(intervaloMin) : null,
    hora: ['diario', 'semanal', 'mensal'].includes(recurrence) ? hora : null,
    dia_semana: recurrence === 'semanal' ? Number(diaSemana) : null,
    dia_mes: recurrence === 'mensal' ? Number(diaMes) : null,
    data_unica: recurrence === 'unica' ? dataUnica : null
  });

  const handleAgendar = async () => {
    if (!canSchedule) {
      return toast.warn('Revise os campos do agendamento.');
    }
    setSavingAgendamento(true);
    try {
      const payload = montarPayloadAgendamento();
      if (editingId) {
        await api.put(`/api/comunicacao/push/agendamentos/${editingId}`, payload);
        toast.success('Agendamento atualizado!');
      } else {
        await api.post('/api/comunicacao/push/agendamentos', payload);
        toast.success('Agendamento criado!');
      }
      resetForm();
      setActiveTab('agendados');
      fetchAgendamentos();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar agendamento.');
    } finally {
      setSavingAgendamento(false);
    }
  };

  const editarAgendamento = (ag) => {
    setEditingId(ag.id);
    setSendMode('agendar');
    setActiveTab('novo');
    setTargetType(ag.alvo || 'todos');
    setSelectedDriver(ag.motorista_id ? String(ag.motorista_id) : '');
    setTitle(ag.titulo || '');
    setMessage(ag.mensagem || '');
    setLink(ag.acao_url || '');
    setCategory(ag.categoria || 'informativo');
    setRecurrence(ag.tipo_recorrencia || 'unica');
    setIntervaloMin(ag.intervalo_min || 15);
    setHora(String(ag.hora || '08:00').slice(0, 5));
    setDiaSemana(ag.dia_semana ?? 1);
    setDiaMes(ag.dia_mes ?? 1);
    setDataUnica(ag.data_unica ? String(ag.data_unica).replace(' ', 'T').slice(0, 16) : '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleAgendamento = async (ag) => {
    try {
      await api.put(`/api/comunicacao/push/agendamentos/${ag.id}/toggle`, { ativo: ag.ativo ? 0 : 1 });
      fetchAgendamentos();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao alterar status.');
    }
  };

  const excluirAgendamento = async (ag) => {
    if (!window.confirm(`Excluir o agendamento "${ag.titulo}"?`)) return;
    try {
      await api.delete(`/api/comunicacao/push/agendamentos/${ag.id}`);
      toast.success('Agendamento excluído.');
      fetchAgendamentos();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir.');
    }
  };

  const catAtual = CATEGORIAS.find(c => c.key === category) || CATEGORIAS[0];

  return (
    <div className="push-page animate-fade">
      <div className="push-header">
        <h1>Central de Comunicação (Push)</h1>
        <p>Envie comunicados, agende disparos e classifique os alertas dos motoristas.</p>
      </div>

      <div className="push-tabs">
        <button className={activeTab === 'novo' ? 'active' : ''} onClick={() => setActiveTab('novo')}>
          <FaPaperPlane /> {editingId ? 'Editando Agendamento' : 'Novo Disparo'}
        </button>
        <button className={activeTab === 'agendados' ? 'active' : ''} onClick={() => setActiveTab('agendados')}>
          <FaCalendarAlt /> Agendamentos
        </button>
        <button className={activeTab === 'historico' ? 'active' : ''} onClick={() => setActiveTab('historico')}>
          <FaHistory /> Histórico de Envios
        </button>
      </div>

      {activeTab === 'novo' && (
        <div className="push-grid">
          {/* Card Form */}
          <div className="push-card">
            <h2><FaMobileAlt /> Configurar Notificação</h2>

            <div className="form-group">
              <label>Público Alvo</label>
              <div className="target-options">
                <div className={`target-card ${targetType === 'todos' ? 'selected' : ''}`} onClick={() => setTargetType('todos')} role="button" tabIndex={0}>
                  <FaUsers /><span>Todos</span>
                </div>
                <div className={`target-card ${targetType === 'online' ? 'selected' : ''}`} onClick={() => setTargetType('online')} role="button" tabIndex={0}>
                  <FaMobileAlt /><span>Online</span>
                </div>
                <div className={`target-card ${targetType === 'especifico' ? 'selected' : ''}`} onClick={() => setTargetType('especifico')} role="button" tabIndex={0}>
                  <FaUser /><span>Específico</span>
                </div>
              </div>
            </div>

            {targetType === 'especifico' && (
              <div className="form-group animate-fade">
                <label>Selecionar Motorista</label>
                <select value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)}>
                  <option value="">-- Escolha o motorista operacional --</option>
                  {motoristas.map(m => (
                    <option key={m.id} value={m.id}>{m.nome} ({m.email})</option>
                  ))}
                </select>
              </div>
            )}

            <div className="form-group">
              <label>Categoria do Aviso</label>
              <div className="cat-options">
                {CATEGORIAS.map(c => (
                  <div
                    key={c.key}
                    className={`cat-card ${c.cls} ${category === c.key ? 'selected' : ''}`}
                    onClick={() => setCategory(c.key)}
                    role="button"
                    tabIndex={0}
                  >
                    <span className="cat-ico">{c.icon}</span>
                    <span>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Título da Notificação</label>
              <input type="text" maxLength={50} placeholder="Ex: Nova OS disponível" value={title} onChange={e => setTitle(e.target.value)} />
              <small className="char-count">{(title || '').length}/50</small>
            </div>

            <div className="form-group">
              <label>Conteúdo da Mensagem</label>
              <textarea rows="4" maxLength={150} placeholder="Digite o texto que aparecerá no celular..." value={message} onChange={e => setMessage(e.target.value)} />
              <small className="char-count">{(message || '').length}/150</small>
            </div>

            <div className="form-group">
              <label><FaLink /> Link de Ação (Opcional)</label>
              <input type="url" placeholder="https://..." value={link} onChange={e => setLink(e.target.value)} />
              <small className="hint">Se vazio, o app abre somente a notificação.</small>
            </div>

            {/* Modo de envio */}
            <div className="form-group">
              <label>Quando enviar</label>
              <div className="segmented">
                <button
                  type="button"
                  className={sendMode === 'agora' ? 'active' : ''}
                  onClick={() => { setSendMode('agora'); setEditingId(null); }}
                  disabled={!!editingId}
                >
                  <FaBolt /> Enviar agora
                </button>
                <button
                  type="button"
                  className={sendMode === 'agendar' ? 'active' : ''}
                  onClick={() => setSendMode('agendar')}
                >
                  <FaCalendarAlt /> Agendar
                </button>
              </div>
            </div>

            {/* Painel de agendamento */}
            {sendMode === 'agendar' && (
              <div className="schedule-box animate-fade">
                <div className="rec-pills">
                  {RECORRENCIAS.map(r => (
                    <button
                      key={r.key}
                      type="button"
                      className={`rec-pill ${recurrence === r.key ? 'active' : ''}`}
                      onClick={() => setRecurrence(r.key)}
                    >
                      {r.icon} <span>{r.label}</span>
                    </button>
                  ))}
                </div>

                <div className="schedule-fields">
                  {recurrence === 'unica' && (
                    <div className="form-group">
                      <label>Data e hora do envio</label>
                      <input type="datetime-local" min={nowLocalInput()} value={dataUnica} onChange={e => setDataUnica(e.target.value)} />
                    </div>
                  )}

                  {recurrence === 'intervalo' && (
                    <div className="form-group">
                      <label>Repetir a cada (minutos)</label>
                      <input type="number" min={15} step={5} value={intervaloMin} onChange={e => setIntervaloMin(e.target.value)} />
                      <small className="hint">Mínimo permitido: 15 minutos.</small>
                    </div>
                  )}

                  {recurrence === 'diario' && (
                    <div className="form-group">
                      <label>Horário (todos os dias)</label>
                      <input type="time" value={hora} onChange={e => setHora(e.target.value)} />
                    </div>
                  )}

                  {recurrence === 'semanal' && (
                    <>
                      <div className="form-group">
                        <label>Dia da semana</label>
                        <div className="weekday-row">
                          {DIAS_SEMANA.map(d => (
                            <button
                              key={d.v}
                              type="button"
                              className={`weekday ${Number(diaSemana) === d.v ? 'active' : ''}`}
                              onClick={() => setDiaSemana(d.v)}
                            >
                              {d.l}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="form-group">
                        <label>Horário</label>
                        <input type="time" value={hora} onChange={e => setHora(e.target.value)} />
                      </div>
                    </>
                  )}

                  {recurrence === 'mensal' && (
                    <>
                      <div className="form-group">
                        <label>Dia do mês (1–31)</label>
                        <input type="number" min={1} max={31} value={diaMes} onChange={e => setDiaMes(e.target.value)} />
                        <small className="hint">Em meses curtos, dispara no último dia disponível.</small>
                      </div>
                      <div className="form-group">
                        <label>Horário</label>
                        <input type="time" value={hora} onChange={e => setHora(e.target.value)} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Botão principal */}
            {sendMode === 'agora' ? (
              <button className="btn-submit-push" onClick={handleSendPush} disabled={loading || !canSend}>
                {loading ? 'Disparando...' : <><FaPaperPlane /> Disparar Push Agora</>}
              </button>
            ) : (
              <button className="btn-submit-push" onClick={handleAgendar} disabled={savingAgendamento || !canSchedule}>
                {savingAgendamento ? 'Salvando...' : <><FaCalendarCheck /> {editingId ? 'Salvar Alterações' : 'Agendar Push'}</>}
              </button>
            )}

            {editingId && (
              <button className="btn-secondary" style={{ width: '100%', marginTop: 10 }} onClick={resetForm}>
                Cancelar edição
              </button>
            )}
          </div>

          {/* Card Preview */}
          <div className="push-card preview-side">
            <h2>Preview no App</h2>
            <div className="phone-mockup">
              <div className="phone-screen">
                <div className="phone-wallpaper" />
                <div className={`notification-bubble ${titleTrim || messageTrim ? 'visible' : ''}`}>
                  <div className="notif-header">
                    <div className="notif-app-info">
                      <div className={`notif-icon ${catAtual.cls}`}>{catAtual.icon}</div>
                      <span>GUINCHO APP</span>
                    </div>
                    <span className="notif-time">Agora</span>
                  </div>
                  <div className="notif-body">
                    <div className="notif-title">{CATEGORIA_EMOJI[category]} {titleTrim || 'Título'}</div>
                    <div className="notif-text">{messageTrim || 'Sua mensagem aparecerá aqui...'}</div>
                    {linkTrim ? <div className="notif-link">Ação: {linkTrim}</div> : null}
                  </div>
                </div>
              </div>
              <div className="phone-home-button" />
            </div>

            {sendMode === 'agendar' && canSchedule && (
              <div className="rec-chip"><FaCalendarCheck /> {descreverRecorrencia(montarPayloadAgendamento())}</div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'agendados' && (
        <div className="push-card animate-fade">
          <div className="history-header">
            <h2>Disparos Agendados</h2>
            <button className="btn-secondary" onClick={fetchAgendamentos} disabled={loadingAgendamentos}>
              {loadingAgendamentos ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>

          {loadingAgendamentos && agendamentos.length === 0 ? (
            <div className="empty-cell">Carregando agendamentos...</div>
          ) : agendamentos.length === 0 ? (
            <div className="empty-cell">Nenhum agendamento criado ainda.</div>
          ) : (
            <div className="agend-list">
              {agendamentos.map(ag => {
                const cat = CATEGORIAS.find(c => c.key === ag.categoria) || CATEGORIAS[0];
                const alvoLabel = ag.alvo === 'especifico' ? (ag.motorista_nome || 'Específico') : String(ag.alvo || '').toUpperCase();
                return (
                  <div key={ag.id} className={`agend-card ${ag.ativo ? '' : 'paused'}`}>
                    <div className="agend-main">
                      <div className="agend-top">
                        <span className={`cat-badge ${cat.cls}`}>{cat.icon} {cat.label}</span>
                        <span className="badge badge-blue">{alvoLabel}</span>
                        {!ag.ativo && <span className="badge badge-yellow">Pausado</span>}
                      </div>
                      <strong className="strong-title">{ag.titulo}</strong>
                      <small className="small-preview">{ag.mensagem}</small>
                      <div className="agend-meta">
                        <span><FaCalendarCheck /> {descreverRecorrencia(ag)}</span>
                        <span><FaClock /> Próximo: {fmtData(ag.proximo_disparo)}</span>
                      </div>
                    </div>
                    <div className="agend-actions">
                      <button className={`icon-btn ${ag.ativo ? 'on' : 'off'}`} title={ag.ativo ? 'Pausar' : 'Reativar'} onClick={() => toggleAgendamento(ag)}>
                        {ag.ativo ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                      <button className="icon-btn edit" title="Editar" onClick={() => editarAgendamento(ag)}><FaEdit /></button>
                      <button className="icon-btn danger" title="Excluir" onClick={() => excluirAgendamento(ag)}><FaTrash /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'historico' && (
        <div className="push-card animate-fade">
          <div className="history-header">
            <h2>Últimos Disparos</h2>
            <button className="btn-secondary" onClick={() => fetchHistorico(1)} disabled={loadingHistory}>
              {loadingHistory ? 'Atualizando...' : 'Atualizar'}
            </button>
          </div>

          {loadingHistory && historico.length === 0 ? (
            <div className="empty-cell">Carregando histórico...</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="history-table">
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Título/Mensagem</th>
                    <th>Categoria</th>
                    <th>Alvo</th>
                    <th>Sucesso/Falha</th>
                  </tr>
                </thead>
                <tbody>
                  {historico.length === 0 ? (
                    <tr><td colSpan="5" className="empty-cell">Nenhum envio registrado.</td></tr>
                  ) : (
                    historico.map(h => {
                      const msg = (h?.mensagem || '');
                      const preview = msg.length > 50 ? `${msg.substring(0, 50)}...` : msg;
                      const label = h.alvo === 'especifico' ? (h.motorista_nome || 'Específico') : String(h.alvo || '').toUpperCase();
                      const bg = h.alvo === 'todos' ? 'badge-blue' : h.alvo === 'online' ? 'badge-green' : 'badge-yellow';
                      const cat = CATEGORIAS.find(c => c.key === h.categoria) || CATEGORIAS[0];
                      return (
                        <tr key={h.id}>
                          <td className="td-date">{h.data_envio ? new Date(h.data_envio).toLocaleString('pt-BR') : '-'}</td>
                          <td>
                            <strong className="strong-title">{h.titulo}</strong>
                            <small className="small-preview">{preview}</small>
                          </td>
                          <td><span className={`cat-badge ${cat.cls}`}>{cat.icon} {cat.label}</span></td>
                          <td><span className={`badge ${bg}`}>{label}</span></td>
                          <td>
                            <div className="result-row">
                              <span className="ok"><FaCheckCircle size={14} /> {h.sucesso ?? 0}</span>
                              <span className="fail"><FaExclamationTriangle size={14} /> {h.falhas ?? 0}</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>

              {totalPages > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px', marginTop: '20px' }}>
                  <button className="btn-secondary" disabled={currentPage === 1 || loadingHistory} onClick={() => fetchHistorico(currentPage - 1)}>
                    <FaChevronLeft /> Anterior
                  </button>
                  <span style={{ fontSize: '13px', color: 'var(--muted)', fontWeight: 900 }}>Página {currentPage} de {totalPages}</span>
                  <button className="btn-secondary" disabled={currentPage === totalPages || loadingHistory} onClick={() => fetchHistorico(currentPage + 1)}>
                    Próxima <FaChevronRight />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}